import { Star, Users, ShoppingCart, Play, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { CATEGORY_COLORS } from "../data/skills";

import { useTranslation } from 'react-i18next';
function Stars({ rating }) {
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < full
              ? "fill-amber-500 text-amber-500"
              : i === full && hasHalf
              ? "fill-amber-500/50 text-amber-500"
              : "text-zinc-700"
          }`}
        />
      ))}
      <span className="ml-1 font-mono text-xs text-zinc-400">{rating}</span>
    </div>
  );
}

function slugify(name) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function SkillCard({ skill, onDemo, onBuy }) {
  const { t } = useTranslation();
  const color = CATEGORY_COLORS[skill.category] || CATEGORY_COLORS.Generic;
  const skillPath = `/skills/${slugify(skill.name)}`;

  return (
    <div className="relative bg-zinc-950 border border-zinc-900 p-6 flex flex-col group hover:border-zinc-700 transition-colors">
      {/* Clickable overlay linking to skill detail */}
      <Link to={skillPath} className="absolute inset-0 z-10" aria-label={`View ${skill.name} details`} />
      {/* Badges row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border border-zinc-700 text-zinc-300" style={{ borderColor: color, color: color }}>
          {skill.category}
        </span>
        {skill.author === "maxemployee" && (
          <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-500 border border-amber-500/20">
            MaxEmployee
          </span>
        )}
        {(skill.isFree || skill.price === 0) && (
          <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{t('skill_card.free')}</span>
        )}
        {skill.isTrial && !skill.isFree && skill.price !== 0 && (
          <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20">
            Trial {skill.trialDays || 7} days
          </span>
        )}
      </div>

      {/* Name */}
      <h3 className="font-display text-xl uppercase leading-tight mb-2 group-hover:text-amber-500 transition-colors">
        {skill.name}
      </h3>

      {/* Description */}
      <p className="text-zinc-400 text-sm leading-relaxed mb-4 flex-1">
        {skill.description}
      </p>

      {/* Social proof */}
      <div className="space-y-2 mb-4">
        <Stars rating={skill.rating} />
        <div className="flex items-center gap-1.5 text-zinc-500">
          <Users className="w-3.5 h-3.5" />
          <span className="font-mono text-xs">{skill.sales || 0} customers using</span>
        </div>
        {skill.testimonial ? (
          <p className="text-zinc-500 text-xs italic border-l-2 border-zinc-800 pl-2">
            "{skill.testimonial}"
          </p>
        ) : null}
      </div>

      {/* Price */}
      <div className="border-t border-zinc-900 pt-4 mb-4">
        {(skill.isFree || skill.price === 0) ? (
          <div className="font-display text-2xl text-emerald-400">Free</div>
        ) : (
          <div>
            <span className="font-display text-2xl text-white">$ {Number(skill.price).toFixed(2)}</span>
            <span className="text-zinc-500 font-mono text-xs ml-1">/mo</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 relative z-20">
        <button
          onClick={() => onDemo(skill)}
          className="flex-1 flex items-center justify-center gap-2 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-4 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors"
        >
          <Play className="w-3.5 h-3.5" />
          See example
        </button>
        <button
          onClick={() => onBuy(skill)}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors ${
            (skill.isFree || skill.price === 0)
              ? "bg-emerald-500 hover:bg-emerald-400 text-black"
              : "bg-amber-500 hover:bg-amber-400 text-black"
          }`}
        >
          {(skill.isFree || skill.price === 0) ? (
            <>
              <Sparkles className="w-3.5 h-3.5" />
              Add
            </>
          ) : (
            <>
              <ShoppingCart className="w-3.5 h-3.5" />
              Buy
            </>
          )}
        </button>
      </div>
    </div>
  );
}
